import { create } from 'zustand';
import { Theme } from '../types/ui/theme';
import { ThemeProvider as Provider } from 'styled-components';
import { merge } from 'lodash';
import { theme } from './theme';
import { useEffect } from 'react';
import {
  ChakraProvider,
  ColorModeScript,
  extendTheme
} from '@chakra-ui/react';
import { useAppSelector } from '../hooks/storeHooks';
import { RtlProvider } from './RtlProvider';
import sessionStorageSingleton from '../configs/sessionStorageSingleton';

// #region state & helpers & types
interface ThemeStore {
  activeTheme: Theme;
  setTheme: (activeTheme: Theme) => void;
}

export type ThemeSchemaType = typeof theme;
export const useThemeStore = create<ThemeStore>((set) => ({
  activeTheme: (localStorage.getItem('theme') as Theme) ?? Theme.Dark,
  setTheme: (activeTheme: Theme) => {
    const userId = sessionStorageSingleton.getUserId();
    if (userId) localStorage.setItem(`${userId}/theme`, activeTheme);
    localStorage.setItem('theme', activeTheme);
    
    set({ activeTheme });
  },
}));

const getTheme = (mode: Theme) =>
  merge({}, theme, {
    colors: theme.colors[mode], //get(theme.colors, mode, theme.colors),
  });

interface ThemeProviderProps {
  children: React.ReactNode;
}
// #endregion

const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { activeTheme, setTheme } = useThemeStore((state) => state);

  const userId = useAppSelector((state) => state.auth.user.id);

  // Each local user will be able to set personalized theme
  useEffect(() => {
    const userTheme = localStorage.getItem(`${userId}/theme`);
    const appTheme = localStorage.getItem('theme');

    if (userTheme && userTheme !== activeTheme) {
      setTheme(activeTheme as Theme);
      localStorage.setItem('theme', activeTheme);
    } else if (!appTheme) {
      const prefersDarkMode =
        window.matchMedia &&
        window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDarkMode && activeTheme !== Theme.Dark) {
        setTheme(Theme.Dark);
      }
    }
  }, [userId]);

  // useEffect(() => {
  //   // if (userId) localStorage.setItem(`${userId}/theme`, activeTheme);

  //   // localStorage.setItem('theme', activeTheme);
  //   console.log("activeTheme", activeTheme)
  // }, [activeTheme]);

  const theme = getTheme(activeTheme);

  // Extending theme for extra functionality
  const chakraTheme = extendTheme({
    config: {
      initialColorMode: activeTheme,
    },
    components: {
      Checkbox: {
        variants: {
          circular: {
            control: {
              borderRadius: 'full',
            },
          },
        },
      },
    },
  });

  return (
    <ChakraProvider theme={chakraTheme}>
      {/* <CSSReset /> */}
      <Provider theme={theme}>
        <ColorModeScript initialColorMode={activeTheme} />
        <RtlProvider>{children}</RtlProvider>
      </Provider>
    </ChakraProvider>
  );
};

export default ThemeProvider;
